import SEO from "../components/SEO";
import LandingNav from "../components/landing/LandingNav";
import LandingFooter from "../components/landing/LandingFooter";
import { Button } from "../components/ui/button";
import { Cookie } from "lucide-react";
import { openCookiePreferences, COOKIE_TABLE } from "../lib/consent";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <SEO title="Privacy & Cookie Policy — hireqix" description="How hireqix collects, uses and protects candidate and account data, your GDPR rights, and the cookies we use." keywords="hireqix privacy policy, cookie policy, GDPR, resume data privacy, candidate data protection" canonicalPath="/privacy" />
      <LandingNav />
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 prose-sm">
        <span className="text-xs font-bold uppercase tracking-widest text-primary">Legal</span>
        <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Privacy &amp; Cookie Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="mt-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
          <Section title="1. Who we are (Data Controller)">
            hireqix operates this resume screening service. For questions about this policy or to exercise any of the rights below,
            contact us at support@hireqix.com. We respond to all requests within 30 days.
          </Section>
          <Section title="2. Data We Collect">
            Account information (name, email, password hash), workspace and job data you create, resumes you upload for evaluation,
            billing records for credit purchases, and basic technical data (IP address and timestamps) used for security and abuse
            prevention. We do not sell your data to third parties.
          </Section>
          <Section title="3. Legal Basis for Processing">
            We process account and workspace data to perform our contract with you, security and abuse-prevention data under our
            legitimate interest in protecting the service, and analytics or marketing data only with your consent (see cookies below).
            Resume data is processed on your instruction as the employer, with you acting as the data controller for candidate data.
          </Section>
          <Section title="4. Resume &amp; Candidate Data (AI Processing)">
            Resumes are processed solely to generate the evaluation you request. Resume content and evaluation results are never used
            to train third-party AI models and are not shared outside your workspace. As the employer you are responsible for having a
            lawful basis to submit candidate resumes to us.
          </Section>
          <Section title="5. Data Retention">
            Resume text and evaluation results are retained for as long as your workspace exists so you can review candidate history
            across batches. Deleting a job removes its candidate evaluations immediately; deleting your account removes every workspace
            you own together with all jobs, resume data and evaluations.
          </Section>
          <Section title="6. Security">
            Passwords are hashed with bcrypt, authentication uses httpOnly session cookies with CSRF protection, sessions can be revoked
            instantly, sensitive endpoints are rate-limited, and all traffic is encrypted in transit via HTTPS.
          </Section>

          <div>
            <h2 className="font-heading text-base font-semibold text-foreground">7. Your Data Rights (GDPR)</h2>
            <p className="mt-1.5">Wherever you live, you can exercise all of the following rights free of charge:</p>
            <ul className="mt-3 space-y-2">
              <RightItem title="Right of access">Ask us for a copy of the personal data we hold about you.</RightItem>
              <RightItem title="Right to rectification">Correct inaccurate account details at any time from your Account page.</RightItem>
              <RightItem title="Right to erasure">Delete your account and all associated data yourself from <span className="font-medium text-foreground">Account &amp; Privacy → Delete my account &amp; data</span>, or email us.</RightItem>
              <RightItem title="Right to data portability">Request a machine-readable export of your account, jobs and evaluations. Candidate results can also be exported as CSV at any time.</RightItem>
              <RightItem title="Right to restrict or object">Ask us to pause processing, or object to processing based on legitimate interest.</RightItem>
              <RightItem title="Right to withdraw consent">Change or withdraw your cookie consent at any time using the button below.</RightItem>
              <RightItem title="Right to complain">Lodge a complaint with your local data protection authority.</RightItem>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-base font-semibold text-foreground">8. Cookies We Use</h2>
            <p className="mt-1.5">
              Strictly necessary cookies are required for you to sign in and stay secure, so they cannot be switched off. Analytics and
              marketing cookies are only ever set after you opt in — nothing non-essential loads before you consent.
            </p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-border/70">
              <table className="w-full text-left text-xs" data-testid="cookie-policy-table">
                <thead className="bg-muted/50 text-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Cookie / storage key</th>
                    <th className="px-3 py-2 font-semibold">Category</th>
                    <th className="px-3 py-2 font-semibold">Purpose</th>
                    <th className="px-3 py-2 font-semibold">Retention</th>
                  </tr>
                </thead>
                <tbody>
                  {COOKIE_TABLE.map((c) => (
                    <tr key={c.name} className="border-t border-border/60">
                      <td className="px-3 py-2 font-mono text-[11px] text-foreground">{c.name}</td>
                      <td className="px-3 py-2">{c.category}</td>
                      <td className="px-3 py-2">{c.purpose}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{c.retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" className="mt-4" onClick={openCookiePreferences} data-testid="manage-cookies-btn">
              <Cookie className="h-4 w-4" /> Manage cookie preferences
            </Button>
          </div>

          <Section title="9. International Transfers &amp; Sub-processors">
            We use trusted providers to run the service: a cloud hosting provider, MongoDB for storage, an AI provider for resume
            evaluation, Resend for transactional email, and Razorpay for payments. Where data is transferred outside your region, it is
            covered by the provider's standard contractual clauses.
          </Section>
          <Section title="10. Contact">
            Privacy questions, access requests and erasure requests can be sent to support@hireqix.com.
          </Section>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-heading text-base font-semibold text-foreground">{title}</h2>
      <p className="mt-1.5">{children}</p>
    </div>
  );
}

function RightItem({ title, children }) {
  return (
    <li className="rounded-lg border border-border/60 bg-muted/30 p-3">
      <span className="font-medium text-foreground">{title}: </span>
      {children}
    </li>
  );
}
